import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withRoomContext } from '../../../RoomContext';
import * as roomActions from '../../../actions/roomActions';
import { firestore } from '../../../lib/firebase';
import Button from '@material-ui/core/Button';
import { MEMO_TEMP, MEMO_CONFIRM, MEMO_TODO_OPEN, ROOM_CALLING } from '../../../const';
import { isZenkakuKana } from '../../../utils';
import ConfirmDialog from '../Common/ConfirmDialog';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';

const styles = () =>
	({
		root :
		{
			width           : '100%',
			height          : '100%',
			position        : 'relative',
			display         : 'flex',
			flexDirection   : 'column',
			overflow        : 'hidden',
			backgroundColor : 'transparent',
			alignItems      : 'center',
			padding         : '0 5%'
		},
		memoWrapper :
		{
			height         : '100%',
			display        : 'flex',
			flexDirection  : 'column',
			justifyContent : 'between'
		},
		topData :
		{
			width         : '100%',
			display       : 'flex',
			height        : '2rem',
			marginTop     : '1rem',
			alignItems    : 'end',
			lineHeight    : 1,
			borderBottom  : '1px solid #999898',
			color         : '#999898',
			paddingBottom : '0.5rem'
		},
		inputLabel :
		{
			textAlign   : 'left',
			fontSize    : '0.8rem',
			width       : '25%',
			whiteSpace  : 'nowrap',
			marginRight : '2%'
		},
		inputValue :
		{
			fontSize   : '0.9rem',
			width      : '80%',
			height     : '1.1rem',
			textAlign  : 'right',
			'&> input' :
			{
				border    : 'none',
				width     : '100%',
				height    : '100%',
				textAlign : 'right',
				outline   : 'none',
				fontSize  : '0.9rem',
				color     : '#999898'
			}
		},
		inputText :
		{
			width         : '100%',
			height        : '100%',
			marginTop     : '5%',
			paddingBottom : '5%',
			'&> textarea' :
			{
				border    : 'none',
				width     : '100%',
				height    : '100%',
				textAlign : 'left',
				resize    : 'none',
				fontSize  : '1rem',
				color     : '#999898',
				padding   : '1%',
				outline   : 'none',
				overflow  : 'auto'
			}
		},
		inputAlert :
		{
			position  : 'absolute',
			fontSize  : '0.6rem',
			color     : 'red',
			textAlign : 'right'
		},
		memoEditHeader :
		{
			width                : '100%',
			borderTopRightRadius : '0.5rem',
			borderTopLeftRadius  : '0.5rem',
			display              : 'flex',
			justifyContent       : 'flex-end',
			alignItems           : 'center',
			'& > button'         : {
				padding     : '2%',
				marginRight : '-0.5rem'
			}
		},
		memoEditDelete :
		{
			color    : '#E57F00',
			fontSize : '1.5rem'
		},
		memoEditContent :
		{
			flexGrow : '1',
			width    : '100%',
			height   : '93%'
		},
		memoEditFooter :
		{
			width          : '98%',
			height         : '2rem',
			display        : 'flex',
			justifyContent : 'space-around',
			alignItems     : 'center',
			textAlign      : 'center',
			fontSize       : '0.7rem'
		},
		saveBtn :
		{
			borderRadius    : '0.3rem',
			backgroundColor : '#99BE18',
			border          : '1px solid #99BE18',
			padding         : '0.1rem',
			color           : '#fff',
			width           : '30%',
			height          : '1.4rem',
			whiteSpace      : 'nowrap',
			fontSize        : '0.7rem',
			'&:hover'       : {
				opacity : 0.8,
				cursor  : 'pointer'
			}
		},
		confirmBtn :
		{
			borderRadius    : '0.3rem',
			backgroundColor : '#E57F00',
			border          : '1px solid #E57F00',
			padding         : '0.1rem',
			color           : '#fff',
			width           : '30%',
			height          : '1.4rem',
			whiteSpace      : 'nowrap',
			fontSize        : '0.7rem',
			'&:hover'       : {
				opacity : 0.8,
				cursor  : 'pointer'
			}
		},
		todoBtn :
		{
			borderRadius    : '0.3rem',
			backgroundColor : 'transparent',
			border          : '1px solid #adadac',
			padding         : '0.1rem',
			color           : '#adadac',
			width           : '30%',
			height          : '1.4rem',
			whiteSpace      : 'nowrap',
			fontSize        : '0.7rem',
			'&:hover'       : {
				opacity : 0.8,
				cursor  : 'pointer'
			}
		},
		removeBtn :
		{
			borderRadius    : '0.3rem',
			backgroundColor : '#f50057',
			border          : '1px solid #f50057',
			padding         : '0.1rem',
			color           : '#fff',
			width           : '30%',
			height          : '1.4rem',
			whiteSpace      : 'nowrap',
			fontSize        : '0.7rem',
			'&:hover'       : {
				opacity : 0.8,
				cursor  : 'pointer'
			}
		}
	});

const Memo = ({
	roomClient,
	classes,
	user,
	room,
	memo,
	onMemoDialog,
	handleCloseOnMemoDialog,
	setMemo,
	btnEnabled,
	style
}) =>
{
	const getDateString = (timestamp) =>
	{
		const date = timestamp ? new Date(timestamp) : new Date();
		const month = (`0${date.getMonth() + 1}`).slice(-2);
		const day = (`0${date.getDate()}`).slice(-2);
		const hour = (`0${date.getHours()}`).slice(-2);
		const min = (`0${date.getMinutes()}`).slice(-2);

		return `${date.getFullYear()}/${month}/${day} ${hour}:${min}`;
	};

	const [ id, setId ] = useState(memo?.id ? memo.id : -1);
	const [ name, setName ] = useState(memo?.name ? memo.name : '');
	const [ timestamp, setTimestamp ] = useState(
		memo?.timestamp ? memo.timestamp : Date.now()
	);
	const [ title, setTitle ] = useState(memo?.title ? memo.title : '');
	const [ message, setMessage ] = useState(memo?.message ? memo.message : '');
	const [ sms, setSMS ] = useState(memo?.sms ? memo.sms : '');
	const [ email, setEmail ] = useState(memo?.email ? memo.email : '');
	const [ guestInput1, setGuestInput1 ] = useState(
		memo?.guestInput1?.type ? memo.guestInput1 : { type: '', key: '', value: '' }
	);
	const [ guestInput2, setGuestInput2 ] = useState(
		memo?.guestInput2?.type ? memo.guestInput2 : { type: '', key: '', value: '' }
	);
	const [ comment, setComment ] = useState(memo ? memo.comment : []);
	const [ status, setStatus ] = useState(memo?.status ? memo.status : MEMO_TEMP);
	const [ showDialog, setShowDialog ] = useState(false);

	const memoRef = useRef();

	useEffect(() =>
	{
		setId(memo?.id ? memo.id : null);
		setName(memo?.name ? memo.name : '');
		setTimestamp(memo?.timestamp ? memo.timestamp : Date.now());
		setTitle(memo?.title ? memo.title : '');
		setMessage(memo?.message ? memo.message : '');
		setSMS(memo?.sms ? memo.sms : '');
		setEmail(memo?.email ? memo.email : '');
		setGuestInput1(
			memo?.guestInput1 ? memo.guestInput1 :
				room?.call?.guestInput1?.type ? room.call.guestInput1
					: { type: '', key: '', value: '' }
		);
		setGuestInput2(
			memo?.guestInput2 ? memo.guestInput2 :
				room?.call?.guestInput2?.type ? room.call.guestInput2
					: { type: '', key: '', value: '' }
		);
		setComment(memo ? memo.comment : []);
		setStatus(memo?.status ? memo.status : '');
	// eslint-disable-next-line
	}, [ memo ]);

	useEffect(() =>
	{
		setMemo({
			id,
			name,
			timestamp,
			title,
			message,
			email,
			sms,
			guestInput1,
			guestInput2,
			comment,
			status
		});

	}, [
		setMemo,
		id,
		name,
		title,
		message,
		email,
		sms,
		guestInput1,
		guestInput2,
		comment,
		timestamp,
		status
	]);

	const saveMemo = useCallback((flag) =>
	{

		if (id)
		{
			if (onMemoDialog && room.call?.duration)
			{
				firestore.collection('memos').doc(id)
					.update({
						name        : name,
						title       : title,
						message     : message,
						sms         : sms,
						email       : email,
						guestInput1 : guestInput1,
						guestInput2 : guestInput2,
						duration    : room.call.duration
					})
					.then(() =>
					{
						if (flag)
						{
							setMemo(null);
							handleCloseOnMemoDialog(false);
						}
					});
			}
			else
			{
				firestore.collection('memos').doc(id)
					.update({
						name        : name,
						title       : title,
						message     : message,
						sms         : sms,
						email       : email,
						guestInput1 : guestInput1,
						guestInput2 : guestInput2
					})
					.then(() =>
					{
						if (flag)
						{
							setMemo(null);
							handleCloseOnMemoDialog(false);
						}
					});
			}
		}
		else
		{
			firestore.collection('memos')
				.add({
					uid         : user.uid,
					user        : (room.info && room.info.user) ? room.info.user : '未設定',
					roomId      : room.name,
					name        : name,
					timestamp   : timestamp,
					title       : title,
					message     : message,
					sms         : sms,
					email       : email,
					guestInput1 : guestInput1,
					guestInput2 : guestInput2,
					comment     : comment ? comment : [],
					status      : status ? status : MEMO_TEMP,
					callId      : room.call.id,
					duration    : room.call.duration
				})
				.then((res) =>
				{
					if (flag)
					{
						setMemo(null);
						handleCloseOnMemoDialog(false);
					}
					else
					{
						setId(res.id);
					}
				});
		}

		roomClient.showNofity('メモを一時保存しました');
	}, [
		roomClient,
		setMemo,
		handleCloseOnMemoDialog,
		comment,
		id,
		message,
		name,
		room.info,
		room.name,
		room.call,
		sms,
		email,
		guestInput1,
		guestInput2,
		status,
		timestamp,
		title,
		user.uid,
		onMemoDialog
	]);

	const confirmMemo = useCallback(() =>
	{
		if (id)
		{
			firestore.collection('memos').doc(id)
				.update({
					name        : name,
					title       : title,
					message     : message,
					sms         : sms,
					email       : email,
					guestInput1 : guestInput1,
					guestInput2 : guestInput2,
					status      : status ? status : MEMO_CONFIRM
				})
				.then(() =>
				{
					setMemo(null);
				});
		}
		else
		{
			firestore.collection('memos')
				.add({
					uid         : user.uid,
					user        : (room.info && room.info.user) ? room.info.user : '未設定',
					roomId      : room.name,
					name        : name,
					timestamp   : timestamp,
					title       : title,
					message     : message,
					sms         : sms,
					email       : email,
					guestInput1 : guestInput1,
					guestInput2 : guestInput2,
					comment     : comment ? comment : [],
					status      : status ? status : MEMO_CONFIRM,
					callId      : room.call.id,
					duration    : room.call.duration
				})
				.then(() =>
				{
					setMemo(null);
					handleCloseOnMemoDialog(false);
				});
		}

		roomClient.showNofity('メモを確定保存しました');
	}, [
		roomClient,
		setMemo,
		handleCloseOnMemoDialog,
		comment,
		id,
		message,
		name,
		room.info,
		room.name,
		room.call,
		email,
		sms,
		guestInput1,
		guestInput2,
		status,
		timestamp,
		title,
		user.uid
	]);

	const moveMemoTodo = useCallback(() =>
	{
		if (id)
		{
			firestore.collection('memos').doc(id)
				.update({
					name        : name,
					title       : title,
					message     : message,
					sms         : sms,
					email       : email,
					guestInput1 : guestInput1,
					guestInput2 : guestInput2,
					status      : status ? status : MEMO_TODO_OPEN
				})
				.then(() =>
				{
					setMemo(null);
				});
		}
		else
		{
			firestore.collection('memos')
				.add({
					uid         : user.uid,
					user        : (room.info && room.info.user) ? room.info.user : '未設定',
					roomId      : room.name,
					name        : name,
					timestamp   : timestamp,
					title       : title,
					message     : message,
					sms         : sms,
					email       : email,
					guestInput1 : guestInput1,
					guestInput2 : guestInput2,
					comment     : comment ? comment : [],
					status      : status ? status : MEMO_TODO_OPEN,
					callId      : room.call.id,
					duration    : room.call.duration
				})
				.then(() =>
				{
					setMemo(null);
					handleCloseOnMemoDialog(false);
				});
		}

		roomClient.showNofity('メモをTODOに移動しました');
	}, [
		roomClient,
		setMemo,
		handleCloseOnMemoDialog,
		comment,
		id,
		message,
		name,
		room.info,
		room.name,
		room.call,
		sms,
		email,
		guestInput1,
		guestInput2,
		status,
		timestamp,
		title,
		user.uid
	]);

	const removeMemo = () =>
	{
		if (id)
		{
			firestore.collection('memos').doc(id)
				.delete()
				.then(() =>
				{
					setMemo(null);
					handleCloseOnMemoDialog(false);
				});
		}
		else
		{
			setMemo(null);
			handleCloseOnMemoDialog(false);
		}

		roomClient.showNofity('メモを削除しました');
	};

	const handleChangeName = (e) =>
	{
		// const tmp = hiraToKata(e.target.value);
		setName(e.target.value);
	};

	const handleChangeSMS = (e) =>
	{
		const numberPattern = /^[0-9]*$/;

		if (numberPattern.test(e.target.value))
		{
			setSMS(e.target.value);
		}
	};

	const handleChangeTitle = (e) =>
	{
		setTitle(e.target.value);
	};

	const handleChangeEmail = (e) =>
	{
		const emailPattern = /^[a-zA-Z0-9_@.-]*$/;

		if (emailPattern.test(e.target.value))
		{
			setEmail(e.target.value);
		}
	};

	const handleChangeGuestInput1 = (e) =>
	{

		setGuestInput1((state) =>
		{
			return {
				...state,
				value : e.target.value
			};
		});

	};

	const handleChangeGuestInput2 = (e) =>
	{

		setGuestInput2((state) =>
		{
			return {
				...state,
				value : e.target.value
			};
		});
	};

	const handleChangeMessage = (e) =>
	{
		setMessage(e.target.value);
	};

	/*
	useEffect(() =>
	{
		setId(null);
		setName('');
		setSMS(room.call ? room.call.sms : '');
		setTimestamp(Date.now());
		setTitle('');
		setMessage('');
		setEmail(room.call ? room.call.email : '');
		setComment([]);
		setStatus('');

		console.log('useEffect', room.call); // eslint-disable-line no-console
	}, [ room.call ]);
	*/

	useEffect(() =>
	{
		if (room.info.status === ROOM_CALLING)
		{
			saveMemo(true);
		}
	}, [ room.info.status ]);

	const isValid = () =>
	{
		return !isZenkakuKana(name);
	};

	return (
		<div className={classes.root} ref={memoRef}>
			{ btnEnabled[5] === true &&
				<div className={classes.memoEditHeader}>
					<IconButton aria-label='delete' onClick={() => setShowDialog(true)}>
						<DeleteIcon className={classes.memoEditDelete}/>
					</IconButton>
				</div>
			}
			<div className={classes.memoWrapper}>
				<div className={classes.memoEditContent}>
					<div className={classes.topData}>
						<div className={classes.inputLabel}>名前</div>
						<div className={classes.inputValue}>
							<input value={name} className={classes.inputField} onChange={(value) => handleChangeName(value)} type='text' />
						</div>
					</div>
					{ !isZenkakuKana(name) &&
					<div className={classes.inputAlert}>※全角カタカナのみ入力可能です</div>
					}
					{ (guestInput1.type !== 'phone' && guestInput2.type !== 'phone') &&
					<div className={classes.topData}>
						<div className={classes.inputLabel}>連絡先</div>
						<div className={classes.inputValue}>
							<input value={sms} className={classes.inputField} onChange={(value) => handleChangeSMS(value)} type='text' />
						</div>
					</div>
					}
					<div className={classes.topData}>
						<div className={classes.inputLabel}>日付</div>
						<div className={classes.inputValue}>
							{getDateString(timestamp)}
						</div>
					</div>
					{	(guestInput1.type !== 'email' && guestInput2.type !== 'email') &&
					<div className={classes.topData}>
						<div className={classes.inputLabel}>Eメール</div>
						<div className={classes.inputValue}>
							<input value={email} className={classes.inputField} onChange={(value) => handleChangeEmail(value)} type='text' />
						</div>
					</div>
					}
					{ guestInput1.key &&
					<div className={classes.topData}>
						<div className={classes.inputLabel}>{guestInput1.key}</div>
						<div className={classes.inputValue}>
							<input
								value={guestInput1.value}
								className={classes.inputField}
								onChange={(e) => handleChangeGuestInput1(e)}
								type='text'
							/>
						</div>
					</div>
					}
					{ guestInput2.key &&
					<div className={classes.topData}>
						<div className={classes.inputLabel}>{guestInput2.key}</div>
						<div className={classes.inputValue}>
							<input
								value={guestInput2.value}
								className={classes.inputField}
								onChange={(e) => handleChangeGuestInput2(e)}
								type='text'
							/>
						</div>
					</div>
					}
					<div className={classes.topData}>
						<div className={classes.inputLabel}>内容</div>
						<div className={classes.inputValue}>
							<input value={title} className={classes.inputField} onChange={(value) => handleChangeTitle(value)} type='text' placeholder='タイトル' />
						</div>
					</div>
					<div className={classes.inputText}>
						<textarea value={message} className={classes.inputField} onChange={(value) => handleChangeMessage(value)} type='text' />
					</div>
				</div>
				<div className={classes.memoEditFooter}>
					{ btnEnabled[0] === true &&
					<Button
						className={classes.saveBtn}
						onClick={() => saveMemo(true)}
						disabled={isValid()}
					>
						一時保存
					</Button>
					}
					{ btnEnabled[1] === true &&
					<Button
						className={classes.confirmBtn}
						onClick={() => confirmMemo()}
						disabled={isValid()}
					>
						確　定
					</Button>
					}
					{ btnEnabled[2] === true &&
					<Button
						className={classes.todoBtn}
						onClick={() => moveMemoTodo()}
						disabled={isValid()}
					>
						TODOへ
					</Button>
					}
					{ btnEnabled[3] === true &&
					<Button
						className={classes.removeBtn}
						onClick={() => removeMemo()}
						disabled={isValid()}
					>
						削除
					</Button>
					}
					{ btnEnabled[4] === true &&
					<Button
						className={classes.saveBtn}
						onClick={() => saveMemo(false)}
						disabled={isValid()}
					>
						一時保存
					</Button>
					}
				</div>
			</div>
			<ConfirmDialog show={showDialog} title='確認' message='本当に削除してよろしいですか' accept={removeMemo} cancel={() => setShowDialog(false)} />
		</div>
	);
};

Memo.propTypes =
{
	roomClient              : PropTypes.any.isRequired,
	user                    : PropTypes.object.isRequired,
	room                    : PropTypes.object.isRequired,
	classes                 : PropTypes.object.isRequired,
	memo                    : PropTypes.object,
	onMemoDialog            : PropTypes.bool,
	setMemo                 : PropTypes.func.isRequired,
	handleCloseOnMemoDialog : PropTypes.func.isRequired,
	btnEnabled              : PropTypes.array.isRequired,
	style                   : PropTypes.object
};

const mapStateToProps = (state) =>
{
	return {
		room : state.room,
		user : state.me.user,
		memo : state.room.memo
	};
};

const mapDispatchToProps = (dispatch) =>
{
	return {
		setMemo : (data) =>
		{
			dispatch(roomActions.setMemo(data));
		},
		handleCloseOnMemoDialog : (flag) =>
		{
			dispatch(roomActions.setOnMemoDialogOpen(flag));
		}
	};
};

export default withRoomContext(connect(
	mapStateToProps,
	mapDispatchToProps,
	null,
	{
		areStatesEqual : (next, prev) =>
		{
			return (
				prev.me.user === next.me.user &&
				prev.room === next.room &&
				prev.room.memo === next.room.memo &&
				prev.room.call === next.room.call
			);
		}
	}
)(withStyles(styles)(Memo)));